<template>
  <div class="max-w-4xl w-full mx-auto space-y-6">
    <div>
      <h1 class="text-2xl font-bold text-gray-900">University Upload Notifications</h1>
      <p class="mt-1 text-sm text-gray-600">
        Manage university-level recipients for source-footage upload lifecycle notifications.
      </p>
    </div>

    <div class="rounded-lg border bg-white p-4">
      <label class="block text-sm font-medium text-gray-700 mb-2">
        Select University
      </label>
      <select
        v-model="selectedUniversity"
        class="w-full h-10 px-3 border rounded-lg"
        @change="onUniversityChange"
      >
        <option disabled :value="null">Select University</option>
        <option
          v-for="university in universities"
          :key="university.id"
          :value="university"
        >
          {{ university.name }}
        </option>
      </select>
    </div>

    <div v-if="selectedUniversity" class="rounded-lg border bg-white p-4">
      <h2 class="text-base font-semibold text-gray-900">
        University Notification Recipients
      </h2>
      <p class="mt-1 text-sm text-gray-600">
        These recipients will receive notifications for all uploads for
        {{ selectedUniversity.name }} when an upload completes and when processing finishes.
      </p>
      <p class="mt-1 text-sm text-gray-600">
        Recipients do not need platform accounts. Enter one email per line.
      </p>

      <textarea
        v-model="uploadNotificationEmailsText"
        rows="8"
        class="mt-4 w-full rounded-lg border p-3"
        :placeholder="'ops@example.com\ngraduation.team@example.com'"
      />

      <div class="mt-4 flex items-center gap-3">
        <button
          type="button"
          class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          @click="save"
          :disabled="saving"
        >
          {{ saving ? "Saving..." : "Save notifications" }}
        </button>
        <span
          v-if="status"
          class="text-sm"
          :class="statusError ? 'text-red-600' : 'text-green-700'"
        >
          {{ status }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { trackAdminEvent } from '../../admin/activity.js'

export default {
  name: "UploadNotifications",
  data() {
    return {
      universities: [],
      selectedUniversity: null,
      uploadNotificationEmailsText: "",
      saving: false,
      status: "",
      statusError: false,
    };
  },
  created() {
    axios
      .get("/universities")
      .then((response) => {
        this.universities = response.data;
      })
      .catch(() => {
        this.$router.push("/admin/login");
      });
  },
  methods: {
    async onUniversityChange() {
      this.status = "";
      this.statusError = false;
      this.uploadNotificationEmailsText = "";
      if (!this.selectedUniversity) {
        return;
      }
      try {
        const response = await axios.post("/admin/search", {
          mode: "upload_notifications",
          action: "get",
          universityId: this.selectedUniversity.id,
        });
        const emails = Array.isArray(response?.data?.uploadNotificationEmails)
          ? response.data.uploadNotificationEmails
          : [];
        this.uploadNotificationEmailsText = emails.join("\n");
      } catch (error) {
        this.status = "Could not load notification recipients";
        this.statusError = true;
      }
    },
    parseEmails() {
      const parts = this.uploadNotificationEmailsText
        .split(/[\n,;]+/)
        .map((value) => value.trim())
        .filter(Boolean);
      const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
      const invalid = parts.filter((value) => !emailRegex.test(value));
      const normalized = [];
      const seen = new Set();
      parts.forEach((value) => {
        const email = value.toLowerCase();
        if (!emailRegex.test(email) || seen.has(email)) {
          return;
        }
        seen.add(email);
        normalized.push(email);
      });
      return { invalid, normalized };
    },
    async save() {
      if (!this.selectedUniversity) {
        return;
      }
      const { invalid, normalized } = this.parseEmails();
      if (invalid.length) {
        this.status = `Invalid email: ${invalid[0]}`;
        this.statusError = true;
        return;
      }

      this.saving = true;
      this.status = "";
      this.statusError = false;
      try {
        const response = await axios.post("/admin/search", {
          mode: "upload_notifications",
          action: "set",
          universityId: this.selectedUniversity.id,
          uploadNotificationEmails: normalized,
        });
        const savedEmails = response?.data?.uploadNotificationEmails || normalized;
        this.uploadNotificationEmailsText = savedEmails.join("\n");
        trackAdminEvent('admin_action_completed', {
          toolKey: 'upload_notifications',
          metadata: {
            action: 'save',
            universityId: this.selectedUniversity.id,
            recipientCount: savedEmails.length,
          },
        })
        this.status = "Saved";
      } catch (error) {
        this.status = "Could not save notifications";
        this.statusError = true;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
